import React, { lazy } from 'react';
import {
  GlobalContextProvider,
  OrdersGlobalContextProvider,
  VFGlobalContextProvider,
  CPPGlobalContextProvider,
  PNGlobalContextProvider,
  AdHocIDGAgendaLoader,
  TaskGlobalContextProvider,
  CertGlobalContextProvider,
  VisitsGlobalContextProvider,
  HopeGlobalContextProvider,
  ReportsGlobalContextProvider,
  HCPCSGlobalContextProvider,
  FormsGlobalContextProvider,
} from '@mummssoftware/global-context';
import { MummsPDFGlobalContextProvider } from '@mummssoftware/mumms-print';
import { PimMessagingContextProvider } from '@mummssoftware/pim-messenger';
import { IDGGlobalContextProvider } from '@mummssoftware/idg';
import {
  ENTITY_MEDICATION,
  ENTITY_MANIFEST,
  ENTITY_PATIENT_CARE_PLAN_FREQUENCY,
  ENTITY_PATIENT_CARE_PLAN_PROBLEM_V2,
  ENTITY_PATIENT_CARE_PLAN_PROBLEM_TASK,
  ENTITY_PATIENT_CARE_GIVER,
  ENTITY_PATIENT_MEASURE,
} from '@mummssoftware/global-context/PNCounter/constants';
import { PNCounterContextProvider } from '@mummssoftware/global-context/PNCounter';
import { Context } from '../../../context';
import { DashboardFaxProvider } from '../../Fax/DashboardFaxProvider';
import { DashboardEmailProvider } from '../../Email/DashboardEmailProvider';

const DashboardContext = lazy(() => import('./DashboardContext'));
const VisitContext = lazy(() => import('./VisitContext'));
const HCPCSContext = lazy(() => import('./HCPCSContext'));

type ProvidersProps = {
  children: React.ReactChild;
  context: Context;
};

class ContextProviders extends React.PureComponent<ProvidersProps> {
  render() {
    const { children, context } = this.props;
    return (
      <GlobalContextProvider
        keycloak={context.keycloak}
        agency={context.agency as string}
        patient={context.patient}
        currentUser={context.currentUser}
      >
        <PNCounterContextProvider
          keycloak={context.keycloak}
          agency={context.agency as string}
          patientNumber={context.patient?.patientNumber as string}
          entityTypes={[
            ENTITY_PATIENT_CARE_PLAN_FREQUENCY,
            ENTITY_PATIENT_CARE_PLAN_PROBLEM_V2,
            ENTITY_PATIENT_CARE_PLAN_PROBLEM_TASK,
            ENTITY_PATIENT_CARE_GIVER,
            ENTITY_MEDICATION,
            ENTITY_MANIFEST,
            ENTITY_PATIENT_MEASURE,
          ]}
        >
          <PNGlobalContextProvider>
            <AdHocIDGAgendaLoader
              pim={context.pim}
              patientNumber={context.patient?.patientNumber}
            />
            <VFGlobalContextProvider>
              <MummsPDFGlobalContextProvider
                keycloak={context.keycloak}
                agency={context.agency as string}
                patient={context.patient}
                currentUser={context.currentUser}
                observation={context.observation}
              >
                <CPPGlobalContextProvider>
                  <TaskGlobalContextProvider
                    siteId={context.siteId}
                    agency={context.agency as string}
                    patient={context.patient}
                    observation={context.observation}
                  >
                    <CertGlobalContextProvider>
                      <PimMessagingContextProvider
                        pim={context.pim}
                        agency={context.agency as string}
                        patient={context.patient}
                      >
                        <IDGGlobalContextProvider
                          observation={context.observation}
                          currentUser={context.currentUser}
                          patient={context.patient}
                        >
                          <OrdersGlobalContextProvider
                            keycloak={context.keycloak}
                            agency={context.agency as string}
                            patient={context.patient}
                            currentUser={context.currentUser}
                            observation={context.observation}
                            workflow={context.workflow}
                            scheduler={context.scheduler}
                          >
                            <VisitsGlobalContextProvider
                              patient={context.patient}
                            >
                              <HopeGlobalContextProvider
                                keycloak={context.keycloak}
                                workflow={context.workflow}
                                patient={context.patient}
                                siteId={context.siteId}
                                agency={context.agency as string}
                                currentUser={context.currentUser}
                                observation={context.observation}
                              >
                                <FormsGlobalContextProvider>
                                  <ReportsGlobalContextProvider
                                    reporting={context.reporting}
                                    agency={context.agency as string}
                                  >
                                    <HCPCSGlobalContextProvider
                                      keycloak={context.keycloak}
                                      agency={context.agency as string}
                                      patient={context.patient}
                                    >
                                      <DashboardEmailProvider>
                                        <DashboardFaxProvider>
                                          <DashboardContext>
                                            <VisitContext>
                                              <HCPCSContext>
                                                {children}
                                              </HCPCSContext>
                                            </VisitContext>
                                          </DashboardContext>
                                        </DashboardFaxProvider>
                                      </DashboardEmailProvider>
                                    </HCPCSGlobalContextProvider>
                                  </ReportsGlobalContextProvider>
                                </FormsGlobalContextProvider>
                              </HopeGlobalContextProvider>
                            </VisitsGlobalContextProvider>
                          </OrdersGlobalContextProvider>
                        </IDGGlobalContextProvider>
                      </PimMessagingContextProvider>
                    </CertGlobalContextProvider>
                  </TaskGlobalContextProvider>
                </CPPGlobalContextProvider>
              </MummsPDFGlobalContextProvider>
            </VFGlobalContextProvider>
          </PNGlobalContextProvider>
        </PNCounterContextProvider>
      </GlobalContextProvider>
    );
  }
}

export default ContextProviders;
